import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, catchError, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { endpoints } from '../../../../environments/endpoints';
import { AuthResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { User } from '../../../Models/avyna/user.model';
import { ApiService } from './api.service';
import { AuthTokenService } from './auth-token.service';
import { ImpersonationFacade } from '../../../Facades/impersonation.facade';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private _apiService = inject(ApiService);
  private _authTokenService = inject(AuthTokenService);
  private _impersonationFacade = inject(ImpersonationFacade);
  private authInitialization$?: Observable<boolean>;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  public authInitialized$ = this.authInitializedSubject.asObservable();

  initializeAuth(): Observable<boolean> {
    if (this.authInitializedSubject.value) {
      return of(this.isAuthenticatedSubject.value);
    }

    if (!this.authInitialization$) {
      this.authInitialization$ = this.bootstrapAuth().pipe(
        map((user) => !!user),
        catchError(() => of(false)),
        tap((is_authenticated) => {
          this.authInitializedSubject.next(true);
          this.isAuthenticatedSubject.next(is_authenticated);
        }),
        shareReplay(1)
      );
    }

    return this.authInitialization$;
  }

  private bootstrapAuth(): Observable<User | null> {
    if (this._authTokenService.hasValidToken()) {
      return this.me().pipe(
        catchError((error) => this.handleInitializationError(error))
      );
    }

    if (this._authTokenService.isImpersonated()) {
      if (window.location.pathname.startsWith('/auth/callback')) {
        this.clearAuth();
        return of(null);
      }

      this._impersonationFacade.endSession();
      return of(null);
    }

    if (this._authTokenService.hasRefreshToken()) {
      return this.refreshToken().pipe(
        switchMap(() => this.me()),
        catchError((error) => this.handleInitializationError(error))
      );
    }

    this.clearAuth();
    return of(null);
  }

  private handleInitializationError(error: unknown): Observable<null> {
    if (this.isAuthenticationError(error)) {
      this.clearAuth();
    }

    return of(null);
  }

  private isAuthenticationError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    return error.status === 401 || error.status === 403;
  }

  /**
   * Store return URL for post-login redirect
   */
  setReturnUrl(returnUrl: string): void {
    this._authTokenService.setReturnUrl(returnUrl);
  }

  exchangeCode(code: string): Observable<User> {
    const request = this._apiService.post<LaravelDefaultResponse<AuthResponse>>(endpoints.auth.exchangeCode, {
      body: { code },
      skipLoading: true,
    });

    return this._apiService.run<LaravelDefaultResponse<AuthResponse>>(request).pipe(
      tap(response => {
        this.setAuth(response.data);
      }),
      switchMap(() => this.me())
    );
  }

  logout(): void {
    const refresh_token = this._authTokenService.getRefreshToken();

    if (refresh_token) {
      this._apiService.runAndForget(
        this._apiService.post<unknown>(endpoints.auth.logout, {
          body: { refresh_token },
        })
      );
    }

    this.clearAuth();
    this.router.navigate(['/auth/login']);
  }

  me(): Observable<User> {
    const request = this._apiService.get<LaravelDefaultResponse<User>>(
      endpoints.api.me.show,
      {
        skipLoading: true,
      }
    );

    return this._apiService.run<LaravelDefaultResponse<User>>(request).pipe(
      map(response => new User(response.data)),
      tap(user => {
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
      })
    );
  }

  setAuth(auth_data: AuthResponse): void {
    this._authTokenService.setToken(auth_data);
    this._impersonationFacade.refreshFromToken();
    this.isAuthenticatedSubject.next(true);
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return this._authTokenService.getToken();
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    return this._authTokenService.isTokenExpired();
  }

  /**
   * Get current user value (synchronous)
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated (synchronous)
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value && this._authTokenService.hasValidToken();
  }

  refreshToken(): Observable<AuthResponse> {
    const refresh_token = this._authTokenService.getRefreshToken();

    if (!refresh_token) {
      throw new Error('No refresh token available.');
    }

    const request = this._apiService.post<LaravelDefaultResponse<AuthResponse>>(
      endpoints.auth.refresh,
      {
        body: { refresh_token },
        skipLoading: true,
      }
    );

    return this._apiService.run<LaravelDefaultResponse<AuthResponse>>(request).pipe(
      map(response => response.data),
      tap(auth_data => {
        this.setAuth(auth_data);
      })
    );
  }

  /**
   * Get stored return URL and clear it
   */
  getAndClearReturnUrl(): string {
    return this._authTokenService.getAndClearReturnUrl();
  }

  clearAuth(): void {
    this._authTokenService.clearToken();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

}
